import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Badge, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiSearch, FiChevronDown, FiChevronRight, FiClipboard } from 'react-icons/fi';
import { TbFlask } from 'react-icons/tb';
import { interpretationsApi } from '../services/api';
import { Interpretation, InterpretationStatus } from '../types';
import { LoadingSpinner, StatusBadge, ConfidenceMeter, EmptyState, Pagination } from '../components/common';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { reviewWindowCell } from '../utils/reviewWindow';
import { companyCell } from '../utils/origin';
import { useCompanies, useInterpretationSearch, useVisiblePolling } from '../hooks';

const PAGE_SIZE = 15;
const POLL_INTERVAL_MS = 30000;

// Searching only what the current page happens to hold makes a patient who exists
// look like a patient who does not. The list is paginated server-side, so BOTH the
// search and the company filter LIVE IN THE SERVER: one request to
// `/interpretations/search` with up to three conditions (term, status, company),
// answered from an index that covers every record (`useInterpretationSearch`), and
// the dropdown's options come from `/interpretations/companies` (`useCompanies`),
// which walks that same index to the end. They used to be a page sweep filtered here
// in the browser; it covered 697 of 976 records, said so with a footer, and no longer
// exists. A record belongs to EVERY company it names (`A · B` is found under either):
// that is the server's `contains` on the stored list, and nothing here re-filters.
const SEARCH_DEBOUNCE_MS = 300;

const InterpretationsList: React.FC = () => {
  const [interpretations, setInterpretations] = useState<Interpretation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<InterpretationStatus | 'all'>('all');

  // Search state. The results come from the server below, not from any page on screen.
  const [debouncedTerm, setDebouncedTerm] = useState('');

  // Trimmed, NOT lowercased: the server normalises case and accents with one rule that
  // the index was written with, and a second copy of that rule here would be the way
  // for the two to disagree.
  const activeTerm = debouncedTerm.trim();
  const isSearching = activeTerm.length > 0;

  // The company filter, and whether the auditor has ever opened its menu. The second
  // one is what keeps `/companies` off the critical path: a page load that never
  // touches this control pays no request for it.
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [companyMenuOpened, setCompanyMenuOpened] = useState(false);
  const requestCompanies = () => setCompanyMenuOpened(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTokens, setPageTokens] = useState<(string | undefined)[]>([undefined]);
  const [totalItems, setTotalItems] = useState(0);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const fetchPage = useCallback(async (pageIndex: number, silent = false, status?: string) => {
    const token = pageTokens[pageIndex];
    if (!silent) {
      setLoadingMore(true);
    }

    try {
      // Use getAll with status filter, or getPending for pending_review
      const response = status === 'pending_review' || status === 'all' || !status
        ? await interpretationsApi.getAll({
            limit: PAGE_SIZE,
            next_token: token,
            status: status === 'all' ? undefined : status || 'pending_review',
          })
        : await interpretationsApi.getAll({
            limit: PAGE_SIZE,
            next_token: token,
            status: status,
          });

      setInterpretations(response.items);
      setTotalItems(response.total);

      // Store next token for next page
      if (response.next_token && !pageTokens[pageIndex + 1]) {
        setPageTokens(prev => {
          const newTokens = [...prev];
          newTokens[pageIndex + 1] = response.next_token;
          return newTokens;
        });
      }
    } catch (error) {
      console.error('Error fetching interpretations:', error);
    } finally {
      if (!silent) {
        setLoadingMore(false);
      }
      setLoading(false);
    }
  }, [pageTokens]);

  // Initial load and when status filter changes
  useEffect(() => {
    // Reset pagination when filter changes
    setPageTokens([undefined]);
    setCurrentPage(1);
    fetchPage(0, false, statusFilter);
  }, [statusFilter]);

  // Debounce the term so typing does not fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // The state options of the dropdown carry `value=""` and are disabled, so the
  // browser never lets them be chosen; the second clause closes it by construction.
  const hasCompanyFilter = companyFilter !== 'all' && companyFilter !== '';
  // Either control puts the list into "results from every record" mode, where the page
  // on screen stops meaning anything: no pagination, no polling.
  const crossPageMode = isSearching || hasCompanyFilter;

  // The dropdown's options: ONE request to `/companies`, the first time the menu is
  // opened (or if a company is already chosen), again on each status change (E06-F02).
  const companies = useCompanies(companyMenuOpened || hasCompanyFilter, statusFilter);
  // The rows of any filtered mode: ONE request to `/search`, answered by the server
  // from its index, with the term and/or the company (E06-F01, E06-F02). The company
  // travels exactly as the option's value -- `null` when none is chosen, never `''`.
  const search = useInterpretationSearch(
    activeTerm,
    statusFilter,
    hasCompanyFilter ? companyFilter : null
  );

  // Polling every 30 seconds -- suspended while the tab is hidden, and paused in any
  // cross-page mode (search OR company filter), since a tick would overwrite those
  // results with a single page.
  // The two conditions compose: `!crossPageMode` travels as `enabled`, and the hook
  // polls only while visible AND enabled.
  useVisiblePolling(
    () => fetchPage(currentPage - 1, true, statusFilter),
    POLL_INTERVAL_MS,
    { enabled: !crossPageMode }
  );

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchPage(page - 1, false, statusFilter);
    }
  };

  // Which source feeds the rows, and therefore whose "busy", "failed" and "truncated"
  // the screen reports: in any filtered mode (term, company, or both) it is the
  // search, whose rows are painted AS RETURNED -- the server already applied every
  // criterion, and re-filtering here would silently drop rows it found. Otherwise the
  // page the server handed over.
  // The search is "busy" until it has either results or a verdict: the one render
  // between a filter landing and the request starting must not flash "nothing found".
  const searchBusy =
    search.searching || (crossPageMode && search.results === null && !search.failed);
  const filteredInterpretations = crossPageMode ? (search.results ?? []) : interpretations;

  const busy = crossPageMode ? searchBusy : loadingMore;
  const sourceFailed = search.failed;

  // The options are the server's, in Spanish collation (the server orders by a
  // normalised key that cannot tell `ñ` from `n`; the browser's collator can). Sorted
  // on a COPY: the hook's state is not ours to reorder.
  const companyOptions = useMemo(
    () => [...(companies.companies ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [companies.companies]
  );

  // R7: the chosen company stays selectable even after a status change drops it from
  // the data. Without this the <select> loses its own value and the browser silently
  // resets it to the first option -- a reset nobody asked for and nobody sees. The
  // orphan option has no count to show (nobody knows it), so it is the only one
  // without parentheses, and only in that transient state.
  const companyChoices =
    hasCompanyFilter && !companyOptions.some((option) => option.name === companyFilter)
      ? [{ name: companyFilter, count: null }, ...companyOptions]
      : companyOptions;

  const hasLabResults = (types: string[]) => types.some(t => t === 'LAB_RESULTS');
  const hasQuestionnaires = (types: string[]) => types.some(t => t !== 'LAB_RESULTS');

  if (loading) {
    return <LoadingSpinner message="Cargando interpretaciones..." />;
  }

  return (
    <div className="interpretations-page">
      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-bar-left">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Buscar paciente, ID o tipo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              aria-label="Estado"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as InterpretationStatus | 'all')
              }
            >
              <option value="all">Todos</option>
              <option value="pending_review">Pendiente</option>
              <option value="approved">Aprobado</option>
              <option value="approved_with_edit">Editado</option>
              <option value="rejected">Rechazadas</option>
            </select>
            <FiChevronDown className="select-icon" />
          </div>

          <div className="filter-select-wrapper">
            {/* onFocus AND onMouseDown: the two ways a native <select> opens, mouse and
                keyboard, and both fire before the browser paints the options. */}
            <select
              className="filter-select filter-select--company"
              aria-label="Empresa"
              value={companyFilter}
              onFocus={requestCompanies}
              onMouseDown={requestCompanies}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="all">Todas las empresas</option>
              {/* Every state of the request says which one it is. A dropdown that went
                  empty after a failure -- including the 404 of a backend that does not
                  have the route yet -- would report "this company has nothing" for
                  what is really "we could not ask" -- the same lie the list refuses
                  to tell about the search. The failure and the empty texts are
                  deliberately different sentences, and the empty one is only stated
                  once the server has actually been asked. There is no "possibly
                  incomplete" footer any more, because the list no longer is. */}
              {companies.loading && (
                <option value="" disabled>
                  Cargando empresas…
                </option>
              )}
              {!companies.loading && companies.failed && (
                <option value="" disabled>
                  No se pudieron cargar las empresas
                </option>
              )}
              {!companies.loading &&
                !companies.failed &&
                companyMenuOpened &&
                companies.companies !== null &&
                companyChoices.length === 0 && (
                  <option value="" disabled>
                    Sin empresa en estas interpretaciones
                  </option>
                )}
              {/* The VALUE is the name exactly as the server stored it -- it is what
                  `/search` compares by equality. The count lives in the text only. */}
              {companyChoices.map(({ name, count }) => (
                <option key={name} value={name}>
                  {count === null ? name : `${name} (${count})`}
                </option>
              ))}
            </select>
            <FiChevronDown className="select-icon" />
          </div>
        </div>
      </div>

      {/* What the search actually covered -- a count the auditor can trust. The
          truncation notice is the server's, with the server's own number from the
          response, whichever filter is active: a company with more than the cap sees
          the first `max_results` and this sentence. */}
      {crossPageMode && !busy && !sourceFailed && (
        <div className="search-summary text-muted small mb-2">
          {filteredInterpretations.length === 0
            ? 'Ningún resultado en todas las interpretaciones'
            : `${filteredInterpretations.length} resultado${
                filteredInterpretations.length === 1 ? '' : 's'
              } en todas las interpretaciones`}
          {search.truncated &&
            ` · se muestran los primeros ${search.maxResults}; afina la búsqueda para ver el resto`}
        </div>
      )}

      {/* List */}
      <Card className="interpretations-list-card">
        {/* `crossPageMode &&` is load-bearing: without it, opening the dropdown with
            the network down would replace a correctly loaded list with an error, even
            though the fifteen rows on screen are perfectly valid. The failure of the
            companies request belongs to the dropdown, which reports it in its own
            options (and never reaches `search.failed`) -- and the filter bar renders
            OUTSIDE this Card, so no existing test would have caught this. */}
        {crossPageMode && sourceFailed ? (
          <Card.Body>
            <EmptyState
              title="No se pudo completar la búsqueda"
              description="Falló la consulta al servidor, así que no sabemos si hay coincidencias. Vuelve a intentarlo."
            />
          </Card.Body>
        ) : filteredInterpretations.length === 0 && !busy ? (
          <Card.Body>
            <EmptyState
              title="No se encontraron interpretaciones"
              description={
                searchTerm || statusFilter !== 'all' || hasCompanyFilter
                  ? 'Intenta ajustar los filtros'
                  : 'No hay interpretaciones pendientes'
              }
            />
          </Card.Body>
        ) : (
          <div className="interpretations-list">
            {/* Header */}
            <div className="list-header">
              <span className="col-patient">Paciente</span>
              <span className="col-company">Empresa</span>
              <span className="col-tests">Tipo</span>
              <span className="col-confidence">Confianza</span>
              <span className="col-status">Estado</span>
              <span className="col-window">Ventana</span>
              {/* Not "Fecha" any more: with two dates in the row, "Fecha" identifies
                  neither of them. This one is when the record was created -- which is
                  explicitly NOT when the review window started. */}
              <span className="col-date">Creada</span>
              <span className="col-action"></span>
            </div>

            {/* Rows */}
            <div className="list-body">
              {busy ? (
                <div className="list-loading">
                  <div className="spinner-border spinner-border-sm text-primary" />
                </div>
              ) : (
                filteredInterpretations.map((interpretation) => {
                  // A search row carries the name at the root (`patient_name`,
                  // derived server-side from the original) and no `raw_data`; a
                  // list row carries `raw_data` and no `patient_name`. One resolution
                  // for both, then the id as the last resort.
                  const patientName =
                    interpretation.patient_name ?? interpretation.raw_data?.patient_info?.name;
                  return (
                    <Link
                      key={interpretation.interpretation_id}
                      to={`/interpretations/${interpretation.interpretation_id}`}
                      className="list-row"
                    >
                      <span className="col-patient">
                        {/* The name column gives up width to the new one and is
                            clipped with an ellipsis; the title is what keeps the full
                            name reachable instead of lost. */}
                        <span
                          className="patient-name"
                          title={patientName || interpretation.patient_id}
                        >
                          {patientName || interpretation.patient_id}
                        </span>
                        {patientName && (
                          <span className="patient-id">{interpretation.patient_id}</span>
                        )}
                      </span>

                      {/* Company: one call into the shared resolution, so this cell
                          and the detail's campaign line cannot disagree about what a
                          record says. It never falls back to `company_id`. */}
                      {(() => {
                        const company = companyCell(interpretation);
                        return (
                          <span className="col-company" title={company.title}>
                            {company.text}
                          </span>
                        );
                      })()}

                      <span className="col-tests">
                        {hasLabResults(interpretation.test_types) && (
                          <span className="test-icon lab" title="Estudios de laboratorio">
                            <TbFlask size={16} />
                          </span>
                        )}
                        {hasQuestionnaires(interpretation.test_types) && (
                          <span className="test-icon quest" title="Cuestionarios">
                            <FiClipboard size={14} />
                          </span>
                        )}
                      </span>

                      <span className="col-confidence">
                        {interpretation.ai_confidence !== undefined ? (
                          <ConfidenceMeter
                            confidence={interpretation.ai_confidence}
                            showLabel={false}
                          />
                        ) : (
                          <span className="no-data">-</span>
                        )}
                      </span>

                      <span className="col-status">
                        <StatusBadge status={interpretation.status || 'pending_review'} />
                        {/* Where an auditor finds the work they left unfinished. Without
                            a mark on the row there is nothing pointing back to it. */}
                        {interpretation.has_draft && (
                          <Badge bg="info" className="ms-1" title="Tiene un borrador sin enviar">
                            Borrador
                          </Badge>
                        )}
                      </span>

                      {/* How long the auditor has left. One call: the level, the text
                          and the tooltip all come from the same resolution, so no date
                          comparison is duplicated inside this component. */}
                      {(() => {
                        const window = reviewWindowCell(interpretation);
                        return (
                          <span
                            className={`col-window window-${window.level}`}
                            title={window.title}
                          >
                            {window.label}
                          </span>
                        );
                      })()}

                      <span className="col-date">
                        {format(new Date(interpretation.created_at), 'dd MMM, HH:mm', { locale: es })}
                      </span>

                      <span className="col-action">
                        <FiChevronRight size={16} />
                      </span>
                    </Link>
                  );
                })
              )}
            </div>

            {/* Pagination -- meaningless while results span every page */}
            {!crossPageMode && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={PAGE_SIZE}
                onPageChange={handlePageChange}
                loading={loadingMore}
              />
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default InterpretationsList;
